import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';

const AuthContext = createContext(null);
const LOGOUT_INCOMPLETE_MSG = 'Sign-out incomplete: we could not end your sessions on the server, so you may still be signed in on other devices or tabs. Please sign in and sign out again in a few minutes.';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = apiClient.getToken();
    // No token = not authenticated — null the user explicitly so callers
    // (e.g. LoginPage's 401 purge) don't depend on a sibling code path to
    // clear stale state (999.2052).
    if (!token) { setUser(null); setLoading(false); return; }

    try {
      const data = await apiClient.get('/api/auth/profile');
      setUser(data.user);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
    } catch {
      apiClient.clearTokens();
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Try cached user first for instant render — but only when an access
    // token exists; a cached identity without tokens sends LoginPage into
    // the authorize-401 redirect loop (999.2052).
    const cached = localStorage.getItem('auth_user');
    if (cached && apiClient.getToken()) {
      try { setUser(JSON.parse(cached)); } catch {}
    }
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const data = await apiClient.post('/api/auth/login', { email, password });
    if (data.requiresTOTP) return data;
    apiClient.setTokens(data.tokens.accessToken, data.tokens.refreshToken);
    setUser(data.user);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    return data;
  };

  // 999.19315 — totpPendingToken is the password step's proof; no userId is sent.
  const loginWithTOTP = async (totpPendingToken, totpCode) => {
    const data = await apiClient.post('/api/auth/login-totp', { totpPendingToken, totpCode });
    apiClient.setTokens(data.tokens.accessToken, data.tokens.refreshToken);
    setUser(data.user);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    return data;
  };

  const register = async (email, password, confirmPassword, name) => {
    return apiClient.post('/api/auth/register', { email, password, confirmPassword, name });
  };

  const oauthLogin = async (provider, providerData) => {
    const data = await apiClient.post(`/api/oauth/${provider}`, providerData);
    apiClient.setTokens(data.tokens.accessToken, data.tokens.refreshToken);
    setUser(data.user);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    return data;
  };

  const logout = async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch {
      // 999.19540 — apiClient throws on non-ok (e.g. 503 LOGOUT_INCOMPLETE) and on network errors
      window.alert(LOGOUT_INCOMPLETE_MSG);
    }
    apiClient.clearTokens();
    setUser(null);
  };

  const value = { user, loading, login, loginWithTOTP, register, oauthLogin, logout, loadUser };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export default AuthContext;
